import { Action, createSelector, Selector, State, StateContext } from '@ngxs/store';
import { catchError, switchMap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { LicenseDetails } from '@microsoft/microsoft-graph-types';
import { Injectable } from '@angular/core';
import { NavigationActions } from '@nimbus-library/features/navigation/navigation.actions';
import { IPhoneNumberNormalizationSettings } from '@nimbus-library/features/user-settings/models/i-phone-number-translation-settings';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { AcUserProfileActions } from '@nimbus-portal/features/attendant-console/store/ac-user-profile/ac-user-profile.actions';
import { AcUserService } from '@nimbus-portal/features/attendant-console/services/ac-user.service';
import { AcTeamMembershipType, AcUser, IAcTeamMembership } from '@nimbus-portal/features/attendant-console/model/ac-user/ac-user.model';
import { RecurringTaskRunner, RecurringTaskRunnerFactory } from '@nimbus-library/features/recurring-task';
import { LicenseSKU } from '@nimbus-portal/features/feature-permission/services/contact-license-capability.model';

export interface IAcUserProfileStateModel {
  isAuthenticated: boolean;
  userInfo: AcUser;
  error?: string;
  teamWidgetState: IAcTeamMembership;
  selectedMembership?: AcTeamMembershipType;
  phoneNormalizationSettings?: IPhoneNumberNormalizationSettings;
  licenses: LicenseDetails[];
  licenseError?: unknown;
  licenseLoading: boolean;
}

@State<IAcUserProfileStateModel>({
  name: 'acUserProfileState',
  defaults: {
    isAuthenticated: false,
    userInfo: null,
    licenses: [],
    licenseLoading: false,
    teamWidgetState: null,
  },
})
@Injectable()
export class AcUserProfileState {
  private loadAttendantStateRunner: RecurringTaskRunner<string>;

  public constructor(
    private readonly acUserService: AcUserService,
    private readonly authenticationService: IAuthenticationService,
    private readonly recurringTaskRunnerFactory: RecurringTaskRunnerFactory,
  ) {}

  @Selector()
  public static userInformation(state: IAcUserProfileStateModel): AcUser {
    return state.userInfo;
  }

  @Selector()
  public static normalizationRule(state: IAcUserProfileStateModel): IPhoneNumberNormalizationSettings {
    return state.teamWidgetState ? state.teamWidgetState.normalizationRule : null;
  }

  @Selector()
  public static activeMembership(state: IAcUserProfileStateModel): AcTeamMembershipType {
    return state.teamWidgetState ? AcTeamMembershipType.Nimbus : null;
  }

  @Selector()
  public static teamWidgetState(state: IAcUserProfileStateModel): IAcTeamMembership {
    return state.teamWidgetState;
  }

  @Selector()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public static hasLicense(licenseOptions: LicenseSKU[]): any {
    return createSelector(
      [AcUserProfileState],
      (state: { acUserProfileState: IAcUserProfileStateModel }) =>
        state.acUserProfileState?.licenses &&
        state.acUserProfileState.licenses.some(
          (licensePlan) =>
            licensePlan.servicePlans &&
            licensePlan.servicePlans.some(
              (servicePlan) =>
                licenseOptions.includes(servicePlan.servicePlanName as LicenseSKU) && servicePlan.provisioningStatus === 'Success',
            ),
        ),
    );
  }

  @Action(AcUserProfileActions.LoadUserInfo)
  public loadUserInfo(
    { dispatch }: StateContext<IAcUserProfileStateModel>,
    { userId }: AcUserProfileActions.LoadUserInfo,
  ): Observable<void> {
    const userInfo = this.authenticationService.getUserInfo();
    const currentUserId = userId || userInfo?.userName;
    if (!currentUserId) {
      return;
    }
    return this.acUserService.getUser().pipe(
      switchMap((result) => dispatch(new AcUserProfileActions.LoadUserInfoSuccess(result))),
      catchError((error) => dispatch(new AcUserProfileActions.LoadUserInfoFailed(error))),
    );
  }

  @Action(AcUserProfileActions.LoadUserInfoSuccess)
  public loadUserInfoSuccess(
    { patchState }: StateContext<IAcUserProfileStateModel>,
    { payload }: AcUserProfileActions.LoadUserInfoSuccess,
  ): void {
    patchState({ userInfo: payload });
  }

  @Action(AcUserProfileActions.LoadUserInfoFailed)
  public loadUserInfoFailed(
    { patchState }: StateContext<IAcUserProfileStateModel>,
    { error }: AcUserProfileActions.LoadUserInfoFailed,
  ): void {
    patchState({ error });
  }

  @Action(AcUserProfileActions.LoadUserLicenseInfo)
  public loadUserLicenseInfo(
    { patchState, dispatch }: StateContext<IAcUserProfileStateModel>,
    {}: AcUserProfileActions.LoadUserLicenseInfo,
  ): Observable<void> {
    patchState({ licenseLoading: true, licenseError: null });
    return this.acUserService.getLicenseInformation().pipe(
      switchMap((result) => dispatch(new AcUserProfileActions.LoadUserLicenseInfoSuccess(result))),
      catchError((error) => dispatch(new AcUserProfileActions.LoadUserLicenseInfoFailed(error))),
    );
  }

  @Action(AcUserProfileActions.LoadUserLicenseInfoSuccess)
  public loadUserLicenseInfoSuccess(
    { patchState }: StateContext<IAcUserProfileStateModel>,
    { payload }: AcUserProfileActions.LoadUserLicenseInfoSuccess,
  ): void {
    patchState({ licenses: payload, licenseLoading: false });
  }

  @Action(AcUserProfileActions.LoadUserLicenseInfoFailed)
  public loadUserLicenseInfoFailed(
    { patchState }: StateContext<IAcUserProfileStateModel>,
    { error }: AcUserProfileActions.LoadUserLicenseInfoFailed,
  ): void {
    patchState({ licenseError: error, licenseLoading: false });
  }

  @Action(AcUserProfileActions.StartLoadAttendantState)
  public startLoadAttendantState(
    { dispatch }: StateContext<IAcUserProfileStateModel>,
    { interval }: AcUserProfileActions.StartLoadAttendantState,
  ): Observable<void> {
    if (!this.loadAttendantStateRunner) {
      this.loadAttendantStateRunner = this.recurringTaskRunnerFactory.createRunner((_parameters, _intervalId, _preload) =>
        dispatch(new AcUserProfileActions.LoadUserInfo()),
      );
    }
    return this.loadAttendantStateRunner.start(undefined, interval);
  }

  @Action(AcUserProfileActions.StopLoadAttendantState)
  public stopLoadAttendantState(): void {
    this.loadAttendantStateRunner.stop(undefined);
  }

  @Action([AcUserProfileActions.LoadTeamsWidgetStates])
  public loadTeamsWidgetStates({ dispatch, patchState }: StateContext<IAcUserProfileStateModel>): Observable<void> {
    const userInfo = this.authenticationService.getUserInfo();
    if (!userInfo) {
      return;
    }
    patchState({ error: null });
    return this.acUserService.getTeamsMembership().pipe(
      switchMap((result) => dispatch(new AcUserProfileActions.LoadTeamsWidgetStatesSuccess(result))),
      catchError((error) => dispatch(new AcUserProfileActions.LoadTeamsWidgetStatesFailed(error))),
    );
  }

  @Action(AcUserProfileActions.LoadTeamsWidgetStatesSuccess)
  public loadTeamsWidgetStatesSuccess(
    { patchState }: StateContext<IAcUserProfileStateModel>,
    { widgetState }: AcUserProfileActions.LoadTeamsWidgetStatesSuccess,
  ): void {
    patchState({ teamWidgetState: widgetState });
  }

  @Action(AcUserProfileActions.LoadTeamsWidgetStatesFailed)
  public loadTeamsWidgetStatesFailed(
    { patchState }: StateContext<IAcUserProfileStateModel>,
    { error }: AcUserProfileActions.LoadTeamsWidgetStatesFailed,
  ): void {
    patchState({ teamWidgetState: null, error });
  }

  @Action(NavigationActions.Navigation)
  public updateMenuStructure(
    { patchState, getState }: StateContext<IAcUserProfileStateModel>,
    { urlAfterRedirects }: NavigationActions.Navigation,
  ): void {
    let newMembership = getState().selectedMembership;

    if (urlAfterRedirects.includes(AcTeamMembershipType.Lucs)) {
      newMembership = AcTeamMembershipType.Lucs;
    } else if (urlAfterRedirects.includes(AcTeamMembershipType.Tm)) {
      newMembership = AcTeamMembershipType.Tm;
    }

    if (getState().selectedMembership !== newMembership) {
      patchState({ selectedMembership: newMembership });
    }
  }
}
